import * as React from 'react';
import { useEffect } from 'react';
import VerificationInput from 'react-verification-input';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ChevronUp, MailOpen, Monitor } from 'lucide-react';
import AuthService from '../../services/AuthService/AuthService';
import MfaResponse from '../../types/MfaResponse';
import UserContext from '../../context/UserContext/UserContext';
import Button from '../../components/Button/Button';
import AuthLayout from '../../components/AuthLayout/AuthLayout';
import AuthErrorAlert from '../../components/AuthLayout/AuthErrorAlert';
import BackToLoginLink from '../../components/AuthLayout/BackToLoginLink';
import maskEmail from '../../utils/maskEmail';
import getDeviceName from '../../utils/getDeviceName';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;
const SUPPORT_EMAIL = 'support@japeto.ai';
const GENERIC_ERROR = 'Something went wrong. Please try again later.';

const HEADING =
  'm-0 mt-4 text-center text-2xl font-extrabold tracking-[-0.5px]';
const SUBTITLE =
  'mx-0 mb-0 mt-2.5 text-center text-sm leading-[1.55] text-auth-muted';
const INLINE_LINK = 'font-bold text-primary underline-offset-4 hover:underline';
const RESEND_BUTTON =
  'border-0 bg-transparent p-0 font-bold text-auth-slate underline underline-offset-2 ' +
  'disabled:cursor-default disabled:text-auth-muted disabled:no-underline';

const ICON_CHIP =
  'mx-auto flex h-[46px] w-[46px] items-center justify-center rounded-xl bg-auth-chip';
const EXPANDER_BUTTON =
  'mx-auto flex items-center gap-[7px] border-0 bg-transparent p-0 text-[13px] ' +
  'font-bold text-auth-link';

const TRUST_EXPLANATION_INTRO = "You'll still be asked for a code if you:";
const TRUST_EXPLANATION_ITEMS = [
  'clear your browser data',
  'sign in from somewhere new',
  'change your password',
  'have your access reset by your administrator'
] as const;

type TwoFaStep = 'code' | 'device';

const CODE_INPUT_CLASSNAMES = {
  container: 'mt-[26px] flex h-auto w-auto justify-center gap-2.5',
  character:
    'flex h-[60px] w-[50px] items-center justify-center rounded-[10px] border-[1.5px] ' +
    'border-auth-border bg-white text-[26px] font-bold text-primary-dark outline-none',
  characterSelected: 'border-brand-light shadow-[0_0_0_1px_#057D99]',
  characterInactive: 'bg-white'
} as const;

export default function TwoFaAuthenticationPage(): React.ReactElement {
  const [step, setStep] = React.useState<TwoFaStep>('code');
  const [code, setCode] = React.useState<string>('');
  const [isVerifying, setIsVerifying] = React.useState<boolean>(false);
  const [isTrusting, setIsTrusting] = React.useState<boolean>(false);
  const [isExplanationOpen, setIsExplanationOpen] =
    React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [resendSecondsLeft, setResendSecondsLeft] = React.useState<number>(
    RESEND_COOLDOWN_SECONDS
  );
  /* Committing the verified user to context would make RequireNoAuth redirect
     immediately, so it is held here until the trust question is answered. */
  const [pendingLogin, setPendingLogin] = React.useState<MfaResponse>();

  const { user, setUser } = React.useContext(UserContext);
  const navigate = useNavigate();
  const hasSentInitialCode = React.useRef(false);
  const deviceName = React.useMemo(
    () => getDeviceName(navigator.userAgent),
    []
  );

  const sendEmail = (signal?: AbortSignal): void => {
    setErrorMessage('');
    setResendSecondsLeft(RESEND_COOLDOWN_SECONDS);
    const optionalArgs = signal ? [signal] : [];
    AuthService.sendEmailVerificationCode(...optionalArgs).catch((err) => {
      if (err.name === 'CanceledError') return;
      setErrorMessage(err.apiError?.message || GENERIC_ERROR);
    });
  };

  const verifyCode = (submittedCode: string): void => {
    if (isVerifying) return;
    setErrorMessage('');
    setIsVerifying(true);
    AuthService.sendMFACode(submittedCode, false)
      .then((responseData) => {
        setPendingLogin(responseData);
        setIsVerifying(false);
        setStep('device');
      })
      .catch((err) => {
        setIsVerifying(false);
        if (err.name === 'CanceledError') return;
        setErrorMessage(err.apiError?.message || GENERIC_ERROR);
      });
  };

  const goToDashboard = (): void => {
    if (pendingLogin) {
      setUser({
        ...pendingLogin.user,
        ...pendingLogin.userAuthStatus
      });
    }
    navigate('/chats');
  };

  const rememberDevice = (): void => {
    setIsTrusting(true);
    /* Trust is best-effort: the user is already authenticated, so a failure
       here must never strand them outside the dashboard. */
    AuthService.trustDevice().then(goToDashboard, goToDashboard);
  };

  useEffect(() => {
    /* This stops the user receiving 2 emails in development mode */
    if (!user || hasSentInitialCode.current) return undefined;
    hasSentInitialCode.current = true;
    const controller = new AbortController();
    sendEmail(controller.signal);
    return (): void => controller.abort();
  }, [user]);

  useEffect(() => {
    if (resendSecondsLeft <= 0) return undefined;
    const timer = setInterval(
      () => setResendSecondsLeft((seconds) => seconds - 1),
      1000
    );
    return (): void => clearInterval(timer);
  }, [resendSecondsLeft]);

  const isResendReady = resendSecondsLeft <= 0;

  const renderCodeStep = (): React.ReactElement => (
    <>
      <div className={ICON_CHIP}>
        <MailOpen className='h-6 w-6 text-brand-light' aria-hidden />
      </div>
      <h1 className={HEADING}>Check your email for a code</h1>
      <p className={SUBTITLE}>
        We sent a {CODE_LENGTH}-digit code to{' '}
        <strong className='text-auth-ink'>
          {user ? maskEmail(user.email) : 'your email'}
        </strong>{' '}
        - it expires in 10 minutes.
      </p>
      <VerificationInput
        length={CODE_LENGTH}
        validChars='0-9'
        placeholder=''
        autoFocus
        value={code}
        onChange={setCode}
        onComplete={verifyCode}
        inputProps={{
          id: 'verification-code',
          name: 'verification-code',
          inputMode: 'numeric',
          autoComplete: 'one-time-code',
          disabled: isVerifying
        }}
        classNames={CODE_INPUT_CLASSNAMES}
      />
      <AuthErrorAlert message={errorMessage} />
      <Button
        type='button'
        size='xl'
        rounded='xl'
        className='mt-5 w-full'
        disabled={code.length < CODE_LENGTH}
        loading={isVerifying}
        onClick={() => verifyCode(code)}
      >
        Verify
      </Button>
      <div className='mt-[18px] text-center text-[13.5px]'>
        <button
          type='button'
          className={RESEND_BUTTON}
          disabled={!isResendReady || isVerifying}
          onClick={() => sendEmail()}
        >
          {isResendReady
            ? 'Resend code'
            : `Resend code in ${resendSecondsLeft}s`}
        </button>
      </div>
      <p className='mx-0 mb-0 mt-[22px] text-center text-[13px] text-auth-muted'>
        Having trouble signing in? Email us at{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`} className={INLINE_LINK}>
          {SUPPORT_EMAIL}
        </a>
      </p>
    </>
  );

  const renderDeviceStep = (): React.ReactElement => (
    <>
      <div className={ICON_CHIP}>
        <Monitor className='h-6 w-6 text-brand-light' aria-hidden />
      </div>
      <h1 className={HEADING}>Should we remember this device?</h1>
      <p className='mx-0 mb-0 mt-3 text-center text-[14.5px] leading-[1.6] text-auth-slate'>
        We can skip the code next time you sign in from{' '}
        <strong>{deviceName}</strong>. You&apos;ll still need a code on any
        other device.
      </p>
      <div className='mt-[26px] flex flex-col gap-2.5'>
        <Button
          type='button'
          size='xl'
          rounded='xl'
          className='h-auto min-h-[46px] whitespace-normal py-2'
          loading={isTrusting}
          onClick={rememberDevice}
        >
          Remember this device for 30 days
        </Button>
        <Button
          type='button'
          variant='outline'
          size='xl'
          rounded='xl'
          className='h-auto min-h-[46px] whitespace-normal border-[1.5px] border-auth-border py-2 font-bold text-auth-slate'
          disabled={isTrusting}
          onClick={goToDashboard}
        >
          Ask me every time
        </Button>
      </div>
      <p className='mx-0 mb-0 mt-3.5 text-center text-[12.5px] leading-normal text-auth-muted'>
        Choose &quot;ask me every time&quot; if this is a shared or public
        computer.
      </p>
      <div className='mt-[22px] border-t border-auth-line pt-4'>
        <button
          type='button'
          className={EXPANDER_BUTTON}
          aria-expanded={isExplanationOpen}
          onClick={() => setIsExplanationOpen((open) => !open)}
        >
          {isExplanationOpen ? (
            <ChevronUp className='h-[15px] w-[15px]' aria-hidden />
          ) : (
            <ChevronDown className='h-[15px] w-[15px]' aria-hidden />
          )}
          What does this mean?
        </button>
        {isExplanationOpen && (
          <div className='mt-4 rounded-[10px] bg-auth-wash px-4 py-4 text-left text-[13px] leading-normal text-auth-slate'>
            <p className='m-0'>{TRUST_EXPLANATION_INTRO}</p>
            <ul className='mb-0 ml-5 mt-2.5 flex list-disc flex-col gap-[7px] p-0'>
              {TRUST_EXPLANATION_ITEMS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );

  return (
    <AuthLayout belowCard={step === 'code' ? <BackToLoginLink /> : undefined}>
      <Helmet>
        <title>Japeto - Two Factor Auth</title>
      </Helmet>
      {step === 'code' ? renderCodeStep() : renderDeviceStep()}
    </AuthLayout>
  );
}
